import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowRight, ArrowLeft, ChevronDown, Youtube, Headphones, Apple } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import SponsorLogo from "@/components/editorial/SponsorLogo";
import OpeningSponsorBand from "@/components/editorial/OpeningSponsorBand";
import ShareButtons from "@/components/editorial/ShareButtons";
import { fetchEpisode } from "@/lib/episodes";

const SITE_URL = "https://mujeres-motor.com";

export default function PodcastDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, notFound: false, data: null });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setState({ loading: true, notFound: false, data: null });
    fetchEpisode(slug)
      .then((data) => {
        setState({ loading: false, notFound: false, data });
        if (data?.episode) {
          // Phase 6.2 · GA4 view_podcast_episode event
          import("@/lib/analytics").then(({ trackEvent }) =>
            trackEvent("view_podcast_episode", { episode_slug: data.episode.slug })
          );
        }
      })
      .catch((e) => setState({ loading: false, notFound: e.response?.status === 404, data: null }));
  }, [slug]);

  if (state.loading) return <SiteLayout><div className="min-h-screen flex items-center justify-center"><p className="text-charcoal/50">Cargando…</p></div></SiteLayout>;
  if (state.notFound) return <SiteLayout><div className="min-h-screen flex flex-col items-center justify-center gap-5"><p className="font-serif text-3xl">Episodio no encontrado</p><button onClick={() => nav("/podcast")} className="underline">Volver al podcast</button></div></SiteLayout>;

  const { episode: ep, related_story: story, episode_sponsor: sponsor, others } = state.data;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "PodcastEpisode",
    url: `${SITE_URL}/podcast/${ep.slug}`,
    name: ep.title,
    episodeNumber: ep.number,
    partOfSeason: { "@type": "PodcastSeason", name: ep.season },
    partOfSeries: { "@type": "PodcastSeries", name: "Mujeres Motor", url: `${SITE_URL}/podcast` },
    datePublished: ep.published_at,
    timeRequired: `PT${ep.duration_minutes}M`,
    description: ep.description,
    image: ep.cover_image_url,
  };

  return (
    <SiteLayout transparent>
      <></>

      {/* Opening sponsor band — cinematic title card */}
      <OpeningSponsorBand
        variant="podcast"
        preferred={sponsor ? [{ ...sponsor, tier: sponsor.tier || "episode" }] : []}
        preferredCategory={story?.industry}
        entityId={ep.id}
      />

      {/* Hero */}
      <section data-testid="podcast-detail-hero" className="relative min-h-[55vh] flex items-end bg-charcoal text-cream overflow-hidden pb-14">
        {ep.cover_image_url && <img src={ep.cover_image_url} alt={ep.title} className="absolute inset-0 w-full h-full object-cover opacity-55" />}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-charcoal/55 to-charcoal" />
        <Container className="relative z-10">
          <Link to="/podcast" className="inline-flex items-center gap-1.5 text-cream/60 text-xs uppercase tracking-[0.2em] hover:text-gold mb-6"><ArrowLeft className="h-3 w-3" />Podcast</Link>
          <p className="text-[10px] uppercase tracking-[0.32em] text-gold mb-4">EP {String(ep.number).padStart(2, "0")} · {ep.season} · {ep.duration_minutes} min</p>
          <h1 className="font-serif font-light text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.03] max-w-4xl">{ep.title}</h1>
          <p className="mt-5 text-cream/75 text-lg max-w-2xl">{ep.guest_name}{ep.guest_role ? ` · ${ep.guest_role}` : ""}</p>
        </Container>
      </section>

      {/* Embed + listen options */}
      <section className="bg-cream-dark py-16 border-t border-charcoal/5">
        <Container>
          <div className="max-w-3xl mx-auto">
            {ep.spotify_embed_url && (
              <div className="rounded-sm overflow-hidden mb-6">
                <iframe src={ep.spotify_embed_url} className="w-full" height="232" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture" loading="lazy" title={ep.title} />
              </div>
            )}
            <div className="flex flex-wrap gap-3 justify-center">
              {ep.youtube_embed_url && (
                <a href={ep.youtube_embed_url} target="_blank" rel="noopener noreferrer" data-testid="ep-youtube" className="inline-flex items-center gap-2 rounded-full bg-[#C4302B] text-cream px-5 py-2.5 text-sm font-medium hover:bg-[#9a2520] transition-colors"><Youtube className="h-4 w-4" />YouTube</a>
              )}
              {ep.spotify_embed_url && (
                <a href={ep.spotify_embed_url.replace("/embed/", "/")} target="_blank" rel="noopener noreferrer" data-testid="ep-spotify" className="inline-flex items-center gap-2 rounded-full bg-[#1DB954] text-charcoal px-5 py-2.5 text-sm font-medium hover:bg-[#1ed760] transition-colors"><Headphones className="h-4 w-4" />Spotify</a>
              )}
              {ep.apple_podcast_url && (
                <a href={ep.apple_podcast_url} target="_blank" rel="noopener noreferrer" data-testid="ep-apple" className="inline-flex items-center gap-2 rounded-full bg-charcoal text-cream px-5 py-2.5 text-sm font-medium hover:bg-charcoal/80 transition-colors"><Apple className="h-4 w-4" />Apple Podcasts</a>
              )}
            </div>
          </div>
        </Container>
      </section>

      {/* Description */}
      <section className="bg-cream py-16 md:py-20">
        <Container>
          <div className="max-w-2xl mx-auto">
            <Eyebrow tone="terracotta" className="mb-5 block">Sobre este episodio</Eyebrow>
            <div className="font-serif text-charcoal text-lg md:text-xl leading-[1.55] whitespace-pre-wrap">{ep.description}</div>
          </div>
        </Container>
      </section>

      {/* Highlights */}
      {ep.highlights && ep.highlights.length > 0 && (
        <section className="bg-cream-dark py-16 border-t border-charcoal/5" data-testid="ep-highlights">
          <Container>
            <div className="max-w-3xl mx-auto">
              <Eyebrow tone="terracotta" className="mb-6 block">Destacados</Eyebrow>
              <div className="space-y-4">
                {ep.highlights.map((h, i) => (
                  <div key={i} className="flex gap-5 p-4 border-l-2 border-terracotta bg-white">
                    <p className="font-mono text-sm text-terracotta min-w-[80px]">{h.timestamp}</p>
                    <p className="text-charcoal/85">{h.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </Container>
        </section>
      )}

      {/* Quote cards */}
      {ep.quote_cards && ep.quote_cards.length > 0 && (
        <section className="bg-cream py-20 md:py-24">
          <Container>
            <div className="max-w-3xl mx-auto space-y-12">
              {ep.quote_cards.map((q, i) => (
                <blockquote key={i} className="border-l-4 border-terracotta pl-7 md:pl-10">
                  <p className="font-serif italic text-3xl md:text-4xl lg:text-5xl font-light text-terracotta leading-[1.2]">"{q.quote}"</p>
                  {q.attribution && <cite className="not-italic block mt-4 text-xs uppercase tracking-[0.28em] text-charcoal/60">— {q.attribution}</cite>}
                </blockquote>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Transcript expandable */}
      {ep.transcript_excerpt && (
        <section className="bg-cream-dark py-16 border-y border-charcoal/5">
          <Container>
            <div className="max-w-3xl mx-auto">
              <button onClick={() => setExpanded((v) => !v)} data-testid="transcript-toggle" className="w-full flex items-center justify-between p-5 bg-white border border-charcoal/10 rounded-sm hover:border-terracotta transition-colors">
                <span className="font-serif text-xl text-charcoal font-light">Leer extracto de la conversación</span>
                <ChevronDown className={`h-4 w-4 text-terracotta transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
              {expanded && (
                <div data-testid="transcript-content" className="mt-6 p-8 bg-white border border-charcoal/10 rounded-sm prose prose-slate max-w-none">
                  <ReactMarkdown>{ep.transcript_excerpt}</ReactMarkdown>
                </div>
              )}
            </div>
          </Container>
        </section>
      )}

      {/* Episode sponsor */}
      {sponsor && (
        <section className="bg-cream py-14" data-testid="ep-sponsor">
          <Container>
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-5">Episodio presentado por</p>
              <div className="inline-flex"><SponsorLogo sponsor={sponsor} tone="light" size="lg" /></div>
            </div>
          </Container>
        </section>
      )}

      {/* Related story */}
      {story && (
        <section className="bg-charcoal text-cream py-20" data-testid="ep-story-link">
          <Container>
            <div className="max-w-3xl mx-auto text-center">
              <Eyebrow tone="gold" className="mb-4 block">Historia completa</Eyebrow>
              <h2 className="font-serif font-light text-3xl md:text-4xl mb-6 leading-[1.1]">Conoce la historia de <span className="italic">{story.name}</span>.</h2>
              <Link to={`/stories/${story.slug}`} className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-6 py-3 text-sm hover:bg-[#A64826]">Leer la historia <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </Container>
        </section>
      )}

      {/* Others */}
      {others && others.length > 0 && (
        <section className="bg-cream py-20 md:py-24">
          <Container>
            <Eyebrow tone="terracotta" className="mb-8 block">Otros episodios</Eyebrow>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {others.map((o) => (
                <Link key={o.id} to={`/podcast/${o.slug}`} className="group block">
                  <div className="aspect-[4/5] overflow-hidden rounded-sm mb-4 bg-charcoal-soft">
                    {o.cover_image_url && <img src={o.cover_image_url} alt={o.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[800ms]" />}
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-1">EP {String(o.number).padStart(2, "0")}</p>
                  <h3 className="font-serif text-xl text-charcoal font-light group-hover:text-terracotta transition-colors">{o.title}</h3>
                </Link>
              ))}
            </div>
          </Container>
        </section>
      )}

      <section className="bg-cream-dark py-10 border-t border-charcoal/5">
        <Container>
          <div className="max-w-2xl mx-auto text-center">
            <ShareButtons url={`${SITE_URL}/podcast/${ep.slug}`} title={ep.title} type="podcast" slug={ep.slug} />
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}
